import { useEffect, useRef, useState } from "react";
import { getRuntimeEnv } from "../config/runtimeEnv";

const DEFAULT_INTERVAL_SECONDS = 300;
const MIN_INTERVAL_SECONDS = 60;
const REQUEST_TIMEOUT_MS = 10_000;

// Intervalo do polling, em segundos. Ausente ou inválida vale o default; um valor válido
// abaixo do piso é elevado a ele — o /version é barato, mas não a ponto de virar heartbeat.
export function resolveVersionCheckIntervalMs(): number {
  const raw = Number(getRuntimeEnv("REACT_APP_VERSION_CHECK_INTERVAL_SECONDS"));
  const seconds = Number.isFinite(raw) && raw > 0 ? Math.max(raw, MIN_INTERVAL_SECONDS) : DEFAULT_INTERVAL_SECONDS;
  return seconds * 1000;
}

async function fetchServedVersion(): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    // no-store: o server já responde sem cache, mas o navegador não pode reaproveitar
    // uma resposta anterior — a pergunta é sempre "qual versão está sendo servida agora".
    const response = await fetch("/version", { cache: "no-store", signal: controller.signal });
    if (!response.ok) return null;
    const body = (await response.json()) as { version?: unknown };
    return typeof body.version === "string" && body.version ? body.version : null;
  } finally {
    clearTimeout(timer);
  }
}

export interface VersionCheckState {
  /** Versão servida na primeira consulta bem-sucedida — o que este bundle assume ser. */
  currentVersion: string | null;
  /** Versão diferente da atual vista numa consulta posterior; `null` enquanto não houver. */
  latestVersion: string | null;
}

/**
 * Consulta `GET /version` periodicamente e detecta um deploy novo.
 *
 * O bundle não carrega a própria versão: a primeira resposta vira o baseline
 * (`currentVersion`) e as seguintes são comparadas com ele. A primeira consulta sai no
 * mount — o hook vive no SoftphoneWorkspace, logo após o check-in —, o que reduz a
 * janela em que um deploy entre o carregamento e o baseline passaria despercebido a
 * segundos.
 *
 * Só consulta com o documento visível: oculto, os timers são throttled e ninguém veria
 * o resultado. Ao voltar a visível consulta na hora — isso cobre o retorno da suspensão
 * do app sem tocar no caminho de resume (o hook escreve só o próprio estado).
 *
 * Uma vez detectada, `latestVersion` fica até o reload; uma falha posterior não a apaga.
 * Falhas são silenciosas por desenho: `console.error` viraria POST na captura remota.
 */
export function useVersionCheck(onUpdateDetected?: (current: string, latest: string) => void): VersionCheckState {
  const [state, setState] = useState<VersionCheckState>({ currentVersion: null, latestVersion: null });
  const baselineRef = useRef<string | null>(null);
  const latestRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const callbackRef = useRef(onUpdateDetected);
  callbackRef.current = onUpdateDetected;

  useEffect(() => {
    let disposed = false;

    const check = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const served = await fetchServedVersion();
        if (disposed || !served) return;

        if (!baselineRef.current) {
          baselineRef.current = served;
          console.log(`[version] baseline ${served}`);
          setState({ currentVersion: served, latestVersion: null });
          return;
        }
        if (served === baselineRef.current || served === latestRef.current) return;

        latestRef.current = served;
        console.log(`[version] atualização disponível: ${baselineRef.current} → ${served}`);
        setState({ currentVersion: baselineRef.current, latestVersion: served });
        callbackRef.current?.(baselineRef.current, served);
      } catch (error) {
        console.debug(`[version] consulta falhou: ${error instanceof Error ? error.message : String(error)}`);
      } finally {
        inFlightRef.current = false;
      }
    };

    const checkIfVisible = () => {
      if (document.visibilityState === "visible") void check();
    };

    void check();
    const interval = setInterval(checkIfVisible, resolveVersionCheckIntervalMs());
    document.addEventListener("visibilitychange", checkIfVisible);

    return () => {
      disposed = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", checkIfVisible);
    };
  }, []);

  return state;
}
